import React, {
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { useLocation } from "react-router";
import {
    ColDef,
    GetRowIdParams,
    SideBarDef,
    StatusPanelDef,
    ToolPanelDef,
} from "ag-grid-enterprise";
import { AgGridReactProps } from "ag-grid-react/lib/shared/interfaces";
import {
    cleanUpQuery,
    getApplyColumnState,
    getFiltersFromQueryString,
    getQueryParamsFromObjects,
} from "./GridHelpers";
import { useLayout } from "../../../_metronic/layout/core";
import {
    CellClickedEvent,
    ColumnVisibleEvent,
    GridApi,
    GridReadyEvent,
    IServerSideDatasource,
    ModelUpdatedEvent,
} from "ag-grid-community";
import StatusBarForServerSide, { IStatusBarForServerSide } from "./StatusBarForServerSide";
import moment from "moment";
const defaultsValueColDef: Partial<ColDef> = {
    sortable: true,
    filter: "agMultiColumnFilter",
    floatingFilter: true,
    suppressMenu: true,
    resizable: true,
};

export interface requestFilters {
    isActive?: number;
    endRow?: number;
    filterModel?: object;
    groupKeys?: any[];
    limit?: number;
    pivotCols?: any[];
    pivotMode?: boolean;
    rowGroupCols?: any[];
    sortModel?: any[];
    startRow?: number;
    valueCols?: any[];
}

export interface ITransition {
    request(): Promise<any>;
    error(currentData: Array<any>, error: any, gridApi: GridApi<any>): void;
    handler(newData: Array<any>, currentData: Array<any>, gridApi: GridApi<any>): void;
}

interface BaseAgGridProps extends AgGridReactProps {
    listHandler: (obj?: requestFilters) => any;
    transition?: ITransition;
    dataSourceLimit?: number;
    totalFilteredHandle?: (obj?: object) => Promise<number>;
    totalRowsHandle?: (obj?: object) => Promise<number>;
    gridRef: RefObject<AgGridReact>;
    toolpanelDef?: (string | ToolPanelDef)[];
    asCellFilter?: boolean;
    defaultFilters?: any;
}

export const BaseAgGrid: React.FC<BaseAgGridProps> = ({
    gridRef,
    columnDefs,
    rowData,
    transition,
    dataSourceLimit = 100,
    asCellFilter = true,
    defaultFilters,
    totalFilteredHandle,
    totalRowsHandle,
    listHandler,
    ...rest
}) => {
    const location = useLocation();
    const oldQuery = useRef("");
    const newQuery = useRef("");

    const [currentDatasetLength, setCurrentDatasetLength] = useState(0);
    const [totalFilteredRows, setTotalFilteredRows] = useState<number | null>(null);
    const [totalRows, setTotalRows] = useState(0);

    const { setIsGrid } = useLayout();

    useLayoutEffect(() => setIsGrid(true));

    useEffect(() => {
        const query = cleanUpQuery(location.search);
        const { filters, sort } = getFiltersFromQueryString(query);
        newQuery.current = query;

        if (gridRef?.current?.api && oldQuery.current !== newQuery.current) {
            oldQuery.current = newQuery.current;
            gridRef.current.api.setFilterModel(filters);
            gridRef.current.columnApi.applyColumnState({
                state: getApplyColumnState(sort ?? []),
            });
        }
    }, [location.search]);

    useEffect(() => {
        if (gridRef.current?.api && rest.rowModelType === "serverSide") {
            const currentDatasetLengthStatusPanel = gridRef.current.api.getStatusPanel(
                "currentDatasetLength"
            ) as IStatusBarForServerSide;

            currentDatasetLengthStatusPanel?.setTotal(currentDatasetLength);
            currentDatasetLengthStatusPanel?.setTotal2(totalFilteredRows);
            currentDatasetLengthStatusPanel?.setType("WITH_TOTAL");
            currentDatasetLengthStatusPanel?.setLabel("Rows");

            const totalRowsStatusPanel = gridRef.current.api.getStatusPanel(
                "totalRows"
            ) as IStatusBarForServerSide;
            totalRowsStatusPanel?.setTotal(totalRows);
            totalRowsStatusPanel?.setLabel("Total Rows");

            const totalFilteredRowsPanel = gridRef.current.api.getStatusPanel(
                "totalFilteredRows"
            ) as IStatusBarForServerSide;
            totalFilteredRowsPanel?.setTotal(totalFilteredRows);
            totalFilteredRowsPanel?.setLabel("Filtered");
        }
    }, [currentDatasetLength, totalFilteredRows, totalRows]);

    const createServerSideDatasource: () => IServerSideDatasource = () => ({
        getRows: async (rowsParams) => {
            const response = await listHandler({
                limit: dataSourceLimit,
                ...rowsParams.request,
            });

            newQuery.current = cleanUpQuery(location.search);
            const currentLength = response?.length;

            let totalResponse = null;
            if (totalFilteredHandle) {
                totalResponse = await totalFilteredHandle({ ...rowsParams.request });
                setTotalFilteredRows(totalResponse);
            }

            const rowCount = !totalResponse
                ? response.length === dataSourceLimit
                    ? undefined
                    : currentLength
                : totalResponse;
            rowsParams.success({
                rowData: response,
                rowCount,
            });
            setCurrentDatasetLength((value) => currentLength + value);
        },
    });

    const onFirstDataRendered = (params: any) => {
        const gridApi = params.api;
        const columnApi = params.columnApi;
        const query = cleanUpQuery(location.search);

        newQuery.current = query;
        oldQuery.current = query;

        const { filters, sort } = getFiltersFromQueryString(query);

        const currentFilters = !filters ? defaultFilters : filters;

        gridApi.setFilterModel(currentFilters);

        if (sort?.length) {
            columnApi.applyColumnState({
                state: getApplyColumnState(sort),
            });
        }
    };

    const gridReadyListener = useCallback(async (params: GridReadyEvent) => {
        const gridApi = params.api;

        gridApi.sizeColumnsToFit();
        //init filters

        const savedGridColumns = window.localStorage.getItem(`${location.pathname}`);

        if (savedGridColumns) {
            gridRef.current?.columnApi?.applyColumnState({
                state: JSON.parse(savedGridColumns),
            });
        }
        //set old query to prevent800size", function () {
        if (gridRef?.current?.api) {
            setTimeout(function () {
                gridRef?.current?.api.sizeColumnsToFit();
            });
        }
        if (rest.rowModelType === "serverSide") {
            const dataSource = createServerSideDatasource();
            gridApi.setServerSideDatasource(dataSource);
        } else {
            const data = await listHandler();
            gridApi.setRowData(data);
            if (transition) {
                transition
                    .request()
                    .then((res: any) => transition.handler(res, data, gridApi))
                    .catch((e) => transition.error(data, e, gridApi));
            }
        }
    }, []);

    const onModelUpdate = useCallback((event: ModelUpdatedEvent) => {
        const query = getQueryParamsFromObjects(
            event.api.getFilterModel(),
            event.columnApi.getColumnState()
        );

        if (oldQuery.current !== query) {
            //set old query to prevent extra navigation push
            setCurrentDatasetLength(0);
            oldQuery.current = query;
            window.history.replaceState(null, "", `?${query}`);
        }
    }, []);

    const onCellDoubleClicked = (event: CellClickedEvent<any>) => {
        if (!asCellFilter) return;
        const fieldName = event.colDef.field;
        if (!fieldName) return;
        let filterModal = {};
        switch (event.colDef.filter) {
            case "agNumberColumnFilter":
                if (typeof event.value == "number")
                    filterModal = {
                        [fieldName]: {
                            type: "equals",
                            filterType: "number",
                            filter: event.value,
                        },
                    };
                break;
            case "agTextColumnFilter":
                filterModal = {
                    [fieldName]: {
                        type: "equals",
                        filterType: "text",
                        filter: event.value,
                    },
                };
                break;
            case "agMultiColumnFilter":
                filterModal = {
                    [fieldName]: {
                        filterModels: [
                            { type: "contains", filterType: "text", filter: event.value },
                        ],
                        filterType: "multi",
                    },
                };
                break;
            case "agDateColumnFilter":
                filterModal = {
                    [fieldName]: {
                        type: "equals",
                        dateFrom: moment(event.value, "DD/MM/YYYY").format("YYYY-MM-DD H:mm:ss"),
                    },
                };
                break;
        }

        const currentFilter = event.api.getFilterModel();
        event.api.setFilterModel({ ...currentFilter, ...filterModal });
    };

    const onColumnVisible = useCallback((event: ColumnVisibleEvent<any>) => {
        window.localStorage.setItem(
            `${location.pathname}`,
            JSON.stringify(event.columnApi.getColumnState())
        );
    }, []);

    const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
        return {
            toolPanels: [
                {
                    id: "columns",
                    labelDefault: "Columns",
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                    toolPanelParams: {
                        suppressRowGroups: false,
                        suppressValues: true,
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressColumnFilter: true,
                        suppressColumnSelectAll: false,
                        suppressColumnExpandAll: false,
                    },
                },
                "filters",
            ],
            hiddenByDefault: false,
        };
    }, []);

    const statusBar = useMemo<{
        statusPanels: StatusPanelDef[];
    }>(() => {
        if (rest.rowModelType !== "serverSide")
            return {
                statusPanels: [
                    { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                    { statusPanel: "agTotalRowCountComponent", align: "center" },
                    { statusPanel: "agFilteredRowCountComponent" },
                    { statusPanel: "agSelectedRowCountComponent" },
                    { statusPanel: "agAggregationComponent" },
                ],
            };

        return {
            statusPanels: [
                {
                    statusPanel: "StatusBarForServerSide",
                    align: "left",
                    key: "currentDatasetLength",
                },
                {
                    statusPanel: "StatusBarForServerSide",
                    align: "center",
                    key: "totalRows",
                },
                {
                    statusPanel: "StatusBarForServerSide",
                    align: "right",
                    key: "totalFilteredRows",
                },
            ],
        };
    }, []);

    useEffect(() => {
        (async () => {
            if (rest.rowModelType === "serverSide" && totalRowsHandle) {
                setTotalRows(await totalRowsHandle());
            }
        })();
    }, []);

    return (
        <AgGridReact
            //CONFIG
            ref={gridRef}
            defaultColDef={defaultsValueColDef}
            serverSideInfiniteScroll
            components={{
                StatusBarForServerSide,
            }}
            //DATA
            columnDefs={columnDefs}
            rowData={rowData}
            //CALLBACKS
            onGridReady={gridReadyListener}
            onModelUpdated={onModelUpdate}
            onCellDoubleClicked={onCellDoubleClicked}
            getRowId={(params: GetRowIdParams) => params.data.id.toString()}
            sideBar={sideBar}
            onColumnVisible={onColumnVisible}
            debounceVerticalScrollbar={true}
            enableRangeSelection={true}
            onFirstDataRendered={onFirstDataRendered}
            statusBar={statusBar}
            rowSelection="multiple"
            //REST of props
            {...rest}
        />
    );
};
